import {UntilDestroy} from '@ngneat/until-destroy';
import {Component, Inject, OnInit} from '@angular/core';
import {WORKBENCH_VIEW_CONFIG, WorkbenchViewConfig} from '../../../shared/types';
import {ArtifactId, BrowserSessionStateId, ConsoleSessionId, DeviceId, ImageUrl, ProjectId} from '@webmate-web-core/shared/types/ids';
import {BrowserSessionInfo, State} from '@webmate-web-core/shared/types/browsersession';
import {BrowserSessionService} from '@webmate-web-core/shared/services/browsersession.service';
import {map, mergeMap, switchMap, take} from 'rxjs/operators';
import {of, Subject} from 'rxjs';
import {Artifact, WellKnownArtifactTypes} from '@webmate-web-core/shared/types/artifact';
import moment from 'moment';
import {ArtifactService} from '@webmate-web-core/shared/services/artifact.service';
import {WorkbenchExpeditionService} from '../../../shared/services/workbench-expedition.service';
import {ImageService} from '@webmate-web-core/shared/services/image.service';
import {WorkbenchViewComponent} from '../../../shared/components/viewcontainers/workbench-view.component';
import {WorkbenchLayoutManager} from '../../../shared/services/workbench-layout-manager.service';
import {WorkbenchContextService} from '../../../shared/services/workbench-context.service';
import {WorkbenchKeyValStore} from '../../../shared/services/workbench-store.service';
import {getStateDisplayLabel} from '@webmate-web-core/shared/utils/interaction-path-display.util';


@UntilDestroy()
@Component({
    templateUrl: './show-expedition-view.component.html',
    styleUrls: ['./show-expedition-view.component.less'],
})
export class ShowExpeditionViewComponent extends WorkbenchViewComponent implements OnInit {

    projectId: ProjectId;

    activeExpedition?: BrowserSessionInfo;

    analysisRunning = false;
    analysisError: string | undefined;

    deviceId: DeviceId;
    consoleSessionId: ConsoleSessionId;

    currentStateId: BrowserSessionStateId | undefined;
    currentStateInfo: State | undefined;

    mayStartAnalysis = false;

    existingStateArtifacts: Artifact[] | undefined;
    existingStatesDropdownItems: { label: string; value: string }[];

    wantRefresh: Subject<void> = new Subject<void>();
    stateIsLoading: boolean = false;

    artifacts: {[artifactId: string]: Artifact} = {};

    constructor(@Inject(WORKBENCH_VIEW_CONFIG) public config: WorkbenchViewConfig<any>,
                private browserSessionService: BrowserSessionService,
                private artifactService: ArtifactService,
                private expeditionService: WorkbenchExpeditionService,
                private imageService: ImageService,
                private store: WorkbenchKeyValStore,
                layoutManager:  WorkbenchLayoutManager,
                private readonly workbenchContextService: WorkbenchContextService) {
        super(layoutManager, config);
    }


    ngOnInit() {
        const workbenchContext = this.workbenchContextService.getCurrentWorkbenchContext()!;
        this.projectId = workbenchContext.project;

        this.workbenchContextService.subscribeWorkbenchContext$().pipe(
            switchMap(context => {
                const expedition = context.expeditionContext?.activeExpedition;
                if (!expedition) {
                    return of({expedition, states: undefined});
                } else {
                    return this.browserSessionService.getBrowserSessionStates(expedition.id, true).pipe(
                        take(1), map(states => ({expedition, states})));
                }
            }),
            mergeMap(({expedition, states}) => {
                if (!states || states.length === 0) {
                    return of({expedition, states: undefined, stateArtifacts: undefined});
                } else {
                    return this.artifactService.getArtifacts$(states).pipe(
                        map(artifacts => {
                            return {states, expedition, stateArtifacts: artifacts}
                        }));
                }
            })
        ).subscribe(({expedition, states, stateArtifacts}) => {
            this.activeExpedition = expedition ? BrowserSessionInfo.fromBackendObject(expedition) : null;
            this.mayStartAnalysis = !!this.activeExpedition && this.activeExpedition.isActive();
            this.existingStateArtifacts = stateArtifacts;
            this.existingStatesDropdownItems = this.existingStateArtifacts
                ?.sort((a, b) => b < a ? -1 : 1)
                ?.map(state => ({label: moment(state.creationTime).format('YYYY-MM-DD H:mm:ss'), value: state.id}));
            if (this.existingStatesDropdownItems?.length > 0 && !this.existingStatesDropdownItems.map(item => item.value).includes(this.currentStateId)) {
                // if we already have a state selected that exists, keep it
                this.selectState(this.existingStatesDropdownItems[0].value);
            }
        });

        this.wantRefresh.next(undefined);
    }

    selectState(stateId: any) {
        const ourStateArtifact = this.existingStateArtifacts.find(s => s.id === stateId);
        this.stateIsLoading = true;
        this.browserSessionService.getState$(ourStateArtifact.id).pipe(take(1)).subscribe(stateInfo => {
            this.stateIsLoading = false;
            this.currentStateInfo = stateInfo;
            this.currentStateId = stateInfo.id;
            this.wantRefresh.next(undefined);
        });
    }

    getCurrentStateLabel(): string {
        return getStateDisplayLabel(this.currentStateInfo?.description, this.currentStateInfo?.provenance?.path)
            || this.currentStateInfo?.description
            || 'Recorded state';
    }

    openArtifact(artifact: Artifact) {
        if (artifact.artifactType === WellKnownArtifactTypes.A11yReport
            || artifact.artifactType === WellKnownArtifactTypes.ContentAnalysis
            || artifact.artifactType === WellKnownArtifactTypes.MobileAccessibilityAnalysis) {
            this.store.setValue('TODO.shownA11yReport', artifact.id);

            // Open WCAG Analysis View
            if(!this.layoutManager.isViewContainerShown("a11y-tools.viewcontainer")){
                this.layoutManager.showViewContainer("a11y-tools.viewcontainer");
            }
            this.layoutManager.setExclusiveViewExpansionState("a11y-tools.view.wcag-analysis", "a11y-tools.viewcontainer");

        }
    }

    loadArtifact(artifactId: ArtifactId) {
        this.artifactService.getArtifact$(artifactId).subscribe(artifact => {
            this.artifacts[artifact.id] = artifact;
        });
    }

    private openedArtifact?: ArtifactId;

    factTabHasChanged(id: ArtifactId) {
        if (this.openedArtifact !== id) {
            this.openedArtifact = id;
            this.loadArtifact(id);
        }
    }

    createSnapshot(event) {
        this.analysisRunning = true;
        this.expeditionService.analyzeStateInActiveExpedition().pipe(mergeMap((stateId: BrowserSessionStateId) => {
            return this.browserSessionService.getState$(stateId).pipe(take(1), map(info => ({info, stateId})))
        })).subscribe({
            next: ({stateId, info}: {stateId: BrowserSessionStateId, info: State}) => {
                this.currentStateId = stateId;
                this.currentStateInfo = info;
                this.analysisRunning = false;
                this.wantRefresh.next(undefined);
            },
            error: (err) => {
                console.error("Error while analyzing state", err['error']);
                this.analysisError = err['error'];
                this.analysisRunning = false;
            }
        });
    }

    makeScreenshotUrl(screenshotId: string): ImageUrl {
        return this.imageService.getImageUrl(screenshotId, 'small');
    }
}
